import {Applicant} from '~/src/models/applicant'
import {ContactInfo, JobDetail, DirectDeposit, SavedFilter} from '../hooks/useCandidateState'
import {BaseApi} from './BaseApi'

// Mock API service for candidate-related operations
// This will be replaced with real API calls when backend is ready

export interface CandidateApiResponse<T> {
  data: T
  success: boolean
  message?: string
}

export interface SubmitComplianceFormsRequest {
  ssn: string
  formType: 'I9' | 'W4' | 'DirectDeposit' | 'EmergencyContact' | 'BackgroundCheck'
  formData: Record<string, any>
  submittedBy?: string
}

export interface CallRecord {
  _id: string
  applicantId: string
  contactName: string
  phoneNumber: string
  callType: 'incoming' | 'outgoing' | 'missed'
  duration: number
  startTime: Date
  endTime: Date
  notes?: string
  createdBy: string
}

export interface TimesheetEntry {
  id: string
  employeeId: string
  weekEnding: Date
  regularHours: number
  overtimeHours: number
  doubletimeHours: number
  ptoHours: number
  status: 'pending' | 'approved' | 'submitted'
}

export interface TaxSetup {
  id: string
  employeeId: string
  jurisdiction: string
  filingStatus: string
  exemptions: number
  additionalWithholding: string
  exempt: boolean
}

export interface PaycheckRecord {
  id: string
  employeeId: string
  payPeriod: string
  payDate: string
  grossPay: number
  taxes: number
  deductions: number
  netPay: number
  status: 'Paid' | 'Pending' | 'Processing'
  checkNumber?: string
}

export class CandidateApiService extends BaseApi {
  // Assignment History
  async getContactInfo(candidateId: string): Promise<CandidateApiResponse<ContactInfo>> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500))

    const mockContactInfo: ContactInfo = {
      homeEmail: 'joe_buttons@email.com',
      workPhone: '952 452 4625',
      employeeNumber: '10',
    }

    return {
      data: mockContactInfo,
      success: true,
      message: 'Contact information retrieved successfully',
    }
  }

  async updateContactInfo(
    candidateId: string,
    contactInfo: Partial<ContactInfo>,
  ): Promise<CandidateApiResponse<ContactInfo>> {
    await new Promise((resolve) => setTimeout(resolve, 800))

    // Mock update logic
    const updated: ContactInfo = {
      homeEmail: 'joe_buttons@email.com',
      workPhone: '952 452 4625',
      employeeNumber: '10',
      ...contactInfo,
    }

    return {
      data: updated,
      success: true,
      message: 'Contact information updated successfully',
    }
  }

  async getJobDetails(candidateId: string): Promise<CandidateApiResponse<JobDetail[]>> {
    await new Promise((resolve) => setTimeout(resolve, 600))

    const mockJobDetails: JobDetail[] = [
      {
        effectiveDate: '07/22/2025 - 07/25/2025',
        jobTitle: 'Forklift Driver',
        assignmentId: '7473995',
        departmentSite: 'Teg Broadway - Floor',
        status: 'Active',
        compensation: '$18 /hrs',
        otRate: '$25 /hrs',
      },
      {
        effectiveDate: '07/22/2025 - 07/25/2025',
        jobTitle: 'Cashier',
        assignmentId: '7473995',
        departmentSite: 'Teg Broadway - Floor',
        status: 'Terminated',
        compensation: '$18 /hrs',
        otRate: '$25 /hrs',
      },
    ]

    return {
      data: mockJobDetails,
      success: true,
      message: 'Job details retrieved successfully',
    }
  }

  async updateJobDetail(
    candidateId: string,
    jobDetailId: string,
    jobDetail: Partial<JobDetail>,
  ): Promise<CandidateApiResponse<JobDetail>> {
    await new Promise((resolve) => setTimeout(resolve, 900))

    const updated: JobDetail = {
      effectiveDate: '07/22/2025 - 07/25/2025',
      jobTitle: 'Forklift Driver',
      assignmentId: '7473995',
      departmentSite: 'Teg Broadway - Floor',
      status: 'Active',
      compensation: '$18 /hrs',
      otRate: '$25 /hrs',
      ...jobDetail,
    }

    return {
      data: updated,
      success: true,
      message: 'Job detail updated successfully',
    }
  }

  async getDirectDepositInfo(candidateId: string): Promise<CandidateApiResponse<DirectDeposit[]>> {
    await new Promise((resolve) => setTimeout(resolve, 550))

    const mockDirectDeposit: DirectDeposit[] = [
      {
        bankName: 'Bank of America',
        accountType: 'Checking Account',
        routingNumber: 'xxxxxx123',
        accountNumber: 'xxxxxxxxxxxxx123',
        percentage: '100%',
        amount: '$1440',
      },
    ]

    return {
      data: mockDirectDeposit,
      success: true,
      message: 'Direct deposit information retrieved successfully',
    }
  }

  async updateDirectDepositInfo(
    candidateId: string,
    directDeposit: Partial<DirectDeposit>,
  ): Promise<CandidateApiResponse<DirectDeposit>> {
    await new Promise((resolve) => setTimeout(resolve, 1000))

    const updated: DirectDeposit = {
      bankName: 'Bank of America',
      accountType: 'Checking Account',
      routingNumber: 'xxxxxx123',
      accountNumber: 'xxxxxxxxxxxxx123',
      percentage: '100%',
      amount: '$1440',
      ...directDeposit,
    }

    return {
      data: updated,
      success: true,
      message: 'Direct deposit information updated successfully',
    }
  }

  // Call Records
  async getCallHistory(candidateId: string): Promise<CandidateApiResponse<CallRecord[]>> {
    await new Promise((resolve) => setTimeout(resolve, 400))

    const mockCallHistory: CallRecord[] = [
      {
        _id: '1',
        applicantId: candidateId,
        contactName: 'John Smith',
        phoneNumber: '555-123-4567',
        callType: 'outgoing',
        duration: 180,
        startTime: new Date('2024-12-10T14:30:00.000Z'),
        endTime: new Date('2024-12-10T14:33:00.000Z'),
        notes: 'Discussed warehouse position availability',
        createdBy: 'recruiter@company.com',
      },
    ]

    return {
      data: mockCallHistory,
      success: true,
      message: 'Call history retrieved successfully',
    }
  }

  async saveCallRecord(
    callData: Omit<CallRecord, '_id'>,
  ): Promise<CandidateApiResponse<CallRecord>> {
    await new Promise((resolve) => setTimeout(resolve, 700))

    const savedCall: CallRecord = {
      ...callData,
      _id: Date.now().toString(),
    }

    return {
      data: savedCall,
      success: true,
      message: 'Call record saved successfully',
    }
  }

  // Timesheets
  async getTimesheets(candidateId: string): Promise<CandidateApiResponse<TimesheetEntry[]>> {
    await new Promise((resolve) => setTimeout(resolve, 500))

    const mockTimesheets: TimesheetEntry[] = [
      {
        id: '1',
        employeeId: candidateId,
        weekEnding: new Date('2024-03-01'),
        regularHours: 40,
        overtimeHours: 8,
        doubletimeHours: 0,
        ptoHours: 0,
        status: 'approved',
      },
    ]

    return {
      data: mockTimesheets,
      success: true,
      message: 'Timesheets retrieved successfully',
    }
  }

  // Tax Setup
  async getTaxSetup(candidateId: string): Promise<CandidateApiResponse<TaxSetup[]>> {
    await new Promise((resolve) => setTimeout(resolve, 450))

    const mockTaxSetup: TaxSetup[] = [
      {
        id: '1',
        employeeId: candidateId,
        jurisdiction: 'Federal',
        filingStatus: 'Single',
        exemptions: 1,
        additionalWithholding: '$0.00',
        exempt: false,
      },
      {
        id: '2',
        employeeId: candidateId,
        jurisdiction: 'California',
        filingStatus: 'Single',
        exemptions: 1,
        additionalWithholding: '$0.00',
        exempt: false,
      },
    ]

    return {
      data: mockTaxSetup,
      success: true,
      message: 'Tax setup retrieved successfully',
    }
  }

  async updateTaxSetup(
    candidateId: string,
    taxSetupId: string,
    taxSetup: Partial<TaxSetup>,
  ): Promise<CandidateApiResponse<TaxSetup>> {
    await new Promise((resolve) => setTimeout(resolve, 800))

    const updated: TaxSetup = {
      id: taxSetupId,
      employeeId: candidateId,
      jurisdiction: 'Federal',
      filingStatus: 'Single',
      exemptions: 1,
      additionalWithholding: '$0.00',
      exempt: false,
      ...taxSetup,
    }

    return {
      data: updated,
      success: true,
      message: 'Tax setup updated successfully',
    }
  }

  // Paychecks
  async getPaychecks(candidateId: string): Promise<CandidateApiResponse<PaycheckRecord[]>> {
    await new Promise((resolve) => setTimeout(resolve, 600))

    const mockPaychecks: PaycheckRecord[] = [
      {
        id: 'PC001',
        employeeId: candidateId,
        payPeriod: '03/01/2024 - 03/15/2024',
        payDate: '03/22/2024',
        grossPay: 1200.0,
        taxes: 240.0,
        deductions: 150.0,
        netPay: 810.0,
        status: 'Paid',
        checkNumber: '12345',
      },
    ]

    return {
      data: mockPaychecks,
      success: true,
      message: 'Paychecks retrieved successfully',
    }
  }

  // Saved Filters
  async getSavedFilters(userId: string): Promise<CandidateApiResponse<SavedFilter[]>> {
    await new Promise((resolve) => setTimeout(resolve, 300))

    const mockSavedFilters: SavedFilter[] = [
      {
        id: '1',
        name: 'Available Warehouse Workers',
        filters: {
          employmentType: ['Full-time'],
          talentStatus: ['Active'],
          workHistorySearch: 'warehouse',
        },
        isGlobal: false,
        isDefault: true,
        createdBy: userId,
        createdAt: new Date('2024-01-15'),
      },
      {
        id: '2',
        name: 'Recently Active Candidates',
        filters: {
          talentStatus: ['Active'],
        },
        isGlobal: true,
        isDefault: false,
        createdBy: 'admin',
        createdAt: new Date('2024-02-01'),
      },
    ]

    return {
      data: mockSavedFilters,
      success: true,
      message: 'Saved filters retrieved successfully',
    }
  }

  async saveFilter(
    filter: Omit<SavedFilter, 'id' | 'createdAt'>,
  ): Promise<CandidateApiResponse<SavedFilter>> {
    await new Promise((resolve) => setTimeout(resolve, 500))

    const savedFilter: SavedFilter = {
      ...filter,
      id: Date.now().toString(),
      createdAt: new Date(),
    }

    return {
      data: savedFilter,
      success: true,
      message: 'Filter saved successfully',
    }
  }

  async deleteFilter(filterId: string): Promise<CandidateApiResponse<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400))

    return {
      data: undefined,
      success: true,
      message: 'Filter deleted successfully',
    }
  }

  /**
   * Submit compliance forms for an applicant
   * POST /applicants/submitComplianceForms
   */
  async submitComplianceForms(
    request: SubmitComplianceFormsRequest,
  ): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.post<Applicant>(
        '/applicants/submitComplianceForms',
        request,
      )
      return {
        data: response.data,
        success: true,
        message: 'Compliance forms submitted successfully',
      }
    } catch (error: unknown) {
      console.error('Error submitting compliance forms:', error)
      throw error
    }
  }

  /**
   * Get applicant by ID
   * GET /applicant/getApplicant/:id
   */
  async getApplicantById(id: string): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.get<Applicant>(`/applicant/getApplicant/${id}`)
      return {
        data: response.data,
        success: true,
        message: 'Applicant fetched successfully',
      }
    } catch (error: unknown) {
      console.error('Error fetching applicant by ID:', id, error)
      throw error
    }
  }

  /**
   * Update applicant
   * PUT /applicant/updateApplicant/:id
   */
  async updateApplicant(
    id: string,
    applicantData: Partial<Applicant>,
  ): Promise<CandidateApiResponse<Applicant>> {
    try {
      const response = await this.client.put<Applicant>(
        `/applicants/updateApplicant`,
        applicantData,
      )
      return {
        data: response.data,
        success: true,
        message: 'Applicant updated successfully',
      }
    } catch (error: unknown) {
      console.error('Error updating applicant:', id, error)
      throw error
    }
  }

  /**
   * Get applicants by service order
   * GET /applicant/getApplicantsByServiceOrder/:serviceOrderId
   */
  async getApplicantsByServiceOrder(
    serviceOrderId: string,
  ): Promise<CandidateApiResponse<Applicant[]>> {
    try {
      const response = await this.client.get<Applicant[]>(
        `/applicant/getApplicantsByServiceOrder/${serviceOrderId}`,
      )
      return {
        data: response.data,
        success: true,
        message: 'Applicants fetched successfully',
      }
    } catch (error: unknown) {
      console.error('Error fetching applicants by service order:', serviceOrderId, error)
      throw error
    }
  }
}

export const candidateApi = new CandidateApiService()
